import {
  BACCARAT_SIDE_BETS,
  VALID_BACCARAT_SIDE_BETS,
  type BaccaratRules,
  type BaccaratSideBetId,
  type BaccaratSpot,
} from '@tv-casino/shared';
import styles from './BaccaratBet.module.css';

/** Chip step for the pair bets: small, because these are long shots. */
const PAIR_STEP = 5;

interface BaccaratBetProps {
  rules: BaccaratRules;
  /** Chips free to stake, before anything staged here. */
  chips: number;
  /** The spot currently picked, or null until the player taps one. */
  spot: BaccaratSpot | null;
  onSpot: (spot: BaccaratSpot) => void;
  pairBets: Partial<Record<BaccaratSideBetId, number>>;
  onPairBets: (next: Partial<Record<BaccaratSideBetId, number>>) => void;
  busy: boolean;
}

/**
 * The whole of a baccarat decision: pick one of three spots. There is nothing
 * to do after the coup deals, so this is the phone's only control — the chip
 * stepper lives beside it, exactly as in blackjack.
 *
 * The sibling of `HoldemActions.tsx`, and a much shorter one.
 */
export function BaccaratBet({
  rules,
  chips,
  spot,
  onSpot,
  pairBets,
  onPairBets,
  busy,
}: BaccaratBetProps) {
  const bankerOdds =
    rules.commission > 0 ? `1:1 − ${(rules.commission * 100).toFixed(0)}%` : '1:1';
  const spots: { id: BaccaratSpot; label: string; odds: string; className?: string }[] = [
    { id: 'player', label: 'Player', odds: '1:1', className: styles.player },
    { id: 'tie', label: 'Tie', odds: `${rules.tiePayout}:1`, className: styles.tie },
    { id: 'banker', label: 'Banker', odds: bankerOdds, className: styles.banker },
  ];

  const staked = Object.values(pairBets).reduce((sum, n) => sum + (n ?? 0), 0);

  const setPair = (id: BaccaratSideBetId, amount: number) => {
    const next = { ...pairBets };
    if (amount <= 0) delete next[id];
    else next[id] = amount;
    onPairBets(next);
  };

  return (
    <section className={styles.wrap}>
      <p className={styles.heading}>Back a spot</p>
      <div className={styles.spots} role="group" aria-label="Baccarat spots">
        {spots.map((s) => (
          <button
            key={s.id}
            type="button"
            className={[styles.spot, s.className, spot === s.id ? styles.spotOn : '']
              .filter(Boolean)
              .join(' ')}
            aria-pressed={spot === s.id}
            disabled={busy}
            onClick={() => onSpot(s.id)}
          >
            <span className={styles.spotName}>{s.label}</span>
            <span className={styles.spotOdds}>{s.odds}</span>
          </button>
        ))}
      </div>

      {rules.pairSideBets && (
        <div className={styles.pairs}>
          <p className={styles.heading}>Pair bets</p>
          {VALID_BACCARAT_SIDE_BETS.map((id) => {
            const def = BACCARAT_SIDE_BETS[id];
            const amount = pairBets[id] ?? 0;
            const room = chips - staked;
            return (
              <div key={id} className={styles.pairRow}>
                <div>
                  <p className={styles.pairName}>{def.name}</p>
                  <p className={styles.pairOdds}>{def.odds}</p>
                </div>
                <div className={styles.stepper}>
                  <button
                    type="button"
                    className={styles.step}
                    disabled={busy || amount === 0}
                    onClick={() => setPair(id, amount - PAIR_STEP)}
                    aria-label={`Lower the ${def.name} bet`}
                  >
                    −
                  </button>
                  <span className={amount > 0 ? styles.amountOn : styles.amount}>{amount}</span>
                  <button
                    type="button"
                    className={styles.step}
                    disabled={busy || amount + PAIR_STEP > rules.sideBetMax || room < PAIR_STEP}
                    onClick={() => setPair(id, amount + PAIR_STEP)}
                    aria-label={`Raise the ${def.name} bet`}
                  >
                    +
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {spot === null && <p className={styles.hint}>Pick a spot, then stake your chips.</p>}
    </section>
  );
}
